import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Card,
  CardContent,
  AppBar,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET, POST } from "APIclient";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import properties from "constants/ApplicationConstants";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";


const IssueAsset = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const assetId = location.state?.assetId;
  const preloadedAsset = location.state?.asset;

  const [assetDetails, setAssetDetails] = useState(preloadedAsset || {});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empSearchText, setEmpSearchText] = useState("");
  const [empLoading, setEmpLoading] = useState(false);
  const [selectedEmployeeOption, setSelectedEmployeeOption] = useState(null);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [employeeRows, setEmployeeRows] = useState([]);
  const [employeeTotal, setEmployeeTotal] = useState(0);
  const [employeePage, setEmployeePage] = useState(0);
  const [employeePageSize, setEmployeePageSize] = useState(20);
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [errors, setErrors] = useState({});
  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");

  const [formData, setFormData] = useState({
    empId: "",
    reason: "",
    assignedDate: new Date().toISOString().split("T")[0],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await GET("admin/employees?page=0&size=10");
        let employeesData = [];
        if (res.data?.data?.paginatedData) {
          employeesData = res.data.data.paginatedData;
        } else if (Array.isArray(res.data?.data)) {
          employeesData = res.data.data;
        } else if (Array.isArray(res.data)) {
          employeesData = res.data;
        }
        const normalized = (employeesData || []).map((emp, idx) => {
          const id = emp.empId || emp.id || emp.employeeId || emp.userId || idx;
          const name = emp.employeeName || emp.empName || emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
          const safeName = (name || "").trim();
          return { id: String(id), label: `${safeName} [${id}]`, raw: emp };
        });
        setEmployees(normalized);
      } catch (err) {
        console.error("Error preloading employees:", err);
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const handler = setTimeout(async () => {
      const q = empSearchText.trim();
      if (q.length < 2) return;
      try {
        setEmpLoading(true);
        const isNumeric = /^\d+$/.test(q);
        const url = `admin/employees?page=0&size=20&empId=${isNumeric ? q : ""}&empName=${!isNumeric ? encodeURIComponent(q) : ""}&status=`;
        const res = await GET(url, { signal: controller.signal });
        let employeesData = [];
        if (res.data?.data?.paginatedData) {
          employeesData = res.data.data.paginatedData;
        } else if (Array.isArray(res.data?.data)) {
          employeesData = res.data.data;
        } else if (Array.isArray(res.data)) {
          employeesData = res.data;
        }
        const normalized = (employeesData || []).map((emp, idx) => {
          const id = emp.empId || emp.id || emp.employeeId || emp.userId || idx;
          const name = emp.employeeName || emp.empName || emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
          const safeName = (name || "").trim();
          return { id: String(id), label: `${safeName} [${id}]`, raw: emp };
        });
        setEmployees(normalized);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Employee search failed:", err);
        }
      } finally {
        setEmpLoading(false);
      }
    }, 400);

    return () => {
      clearTimeout(handler);
      controller.abort();
    };
  }, [empSearchText]);

  const employeeColumns = [
    { field: "empId", headerName: "Emp ID", width: 120 },
    { field: "employeeName", headerName: "Employee Name", width: 260 },
    { field: "designation", headerName: "Designation", width: 160 },
    { field: "officialEmailAddress", headerName: "Official Email", width: 240 }
  ];

  const fetchEmployeePage = async (page = employeePage, size = employeePageSize, query = employeeQuery) => {
    try {
      setEmpLoading(true);
      const safeQuery = query || "";
      const isNumeric = /^\d+$/.test(safeQuery);
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        empId: isNumeric ? safeQuery : "",
        empName: !isNumeric ? safeQuery : "",
        status: ""
      });
      const res = await GET(`admin/employees?${params.toString()}`);
      let list = [];
      let total = 0;
      if (res.data?.data?.paginatedData) {
        list = res.data.data.paginatedData;
        total = res.data.data.totalItems || 0;
      } else if (Array.isArray(res.data?.data)) {
        list = res.data.data;
        total = list.length;
      } else if (Array.isArray(res.data)) {
        list = res.data;
        total = list.length;
      }
      const rows = list.map((emp, idx) => {
        const idVal = emp.empId || emp.id || emp.employeeId || (emp.userId ?? "");
        const nameVal = emp.employeeName || emp.empName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
        return {
          _rid: page * size + idx + 1,
          empId: idVal,
          employeeName: nameVal,
          designation: emp.designation,
          officialEmailAddress: emp.officialEmailAddress,
          ...emp
        };
      });
      setEmployeeRows(rows);
      setEmployeeTotal(total);
    } catch (err) {
      console.error("Failed to fetch employees page:", err);
      setEmployeeRows([]);
      setEmployeeTotal(0);
    } finally {
      setEmpLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (employeeDialogOpen) {
      fetchEmployeePage(0, employeePageSize, employeeQuery);
      setEmployeePage(0);
    }
  }, [employeeDialogOpen]);

  useEffect(() => {
    if (preloadedAsset && preloadedAsset.assetsId) return;
    if (assetId) {
      const fetchAssetDetails = async () => {
        try {
          const res = await GET(`it-dept/assets/${assetId}`);
          setAssetDetails(res.data.data || {});
        } catch (err) {
          console.error("Error fetching asset:", err);
        }
      };
      fetchAssetDetails();
    }
  }, [assetId, preloadedAsset]);


  const handleIssue = async () => {
    try {
      setLoading(true);
      const actualAssetId = assetId || assetDetails?.assetsId || assetDetails?.assetId || assetDetails?.id;
      const validationErrors = {};
      if (!formData.empId) validationErrors.empId = "Employee is required";
      if (!formData.assignedDate) validationErrors.assignedDate = "Assigned date is required";
      if (!actualAssetId) validationErrors.assetId = "Asset ID is required";
      if (!selectedEmployeeOption) validationErrors.empId = "Selected employee not found";

      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        setLoading(false);
        return;
      }
      const empName = selectedEmployeeOption?.raw?.employeeName ||
        selectedEmployeeOption?.raw?.empName ||
        selectedEmployeeOption?.raw?.fullName ||
        selectedEmployeeOption?.raw?.name ||
        (selectedEmployeeOption?.raw?.firstName && selectedEmployeeOption?.raw?.lastName ?
          `${selectedEmployeeOption.raw.firstName} ${selectedEmployeeOption.raw.lastName}` : '') ||
        selectedEmployeeOption?.label?.split(' [')[0] || '';
      const loggedInUserName = window.sessionStorage.getItem("EmpName") || "IT Admin";
      const payload = {
        empId: parseInt(formData.empId),
        empName: empName,
        assetsId: String(actualAssetId),
        assignedDate: formData.assignedDate,
        assignedBy: loggedInUserName,
        reason: formData.reason || '',
        project: assetDetails?.project || '',
        assetBy: assetDetails?.assetBy || '',
      };
      const res = await POST("it-dept/asset/issue_asset", payload);
      if (res.data?.status === "CREATED" || res.data?.statusCode === 201 || res.data?.success) {
        setSnakColor("success");
        setSnakMessage("Asset assigned successfully");
        setOpen(true);

        setTimeout(() => {
          navigate("/assetList");
        }, 1500);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (err) {
      const statusCode = err?.response?.status;
      if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Asset is already assigned and cannot be issued again.");
      } else if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(err?.message || "Unexpected error occurred.");
      }
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox
              pt={2}
              mb={0}
              pb={1}
              px={4}
              sx={{ borderRadius: "5px", border: "1px solid #D3D3D3" }}
            >
              <MDTypography
                variant="h6"
                sx={{
                  color: "textPrimary",
                  fontFamily: properties.fontFamily,
                  fontSize: "17px",
                  pb: 1,
                  fontWeight: "bold"
                }}
              >
                Issue Asset
              </MDTypography>

              <Grid container spacing={2}>
                {/* <Grid item xs={12}>
                  <MDTypography
                    variant="h6"
                    sx={{ fontFamily: properties.fontFamily, fontWeight: "bold" }}
                  >
                    Asset Information
                  </MDTypography>
                </Grid> */}

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Component Name
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails?.itComponent?.componentName ||
                        assetDetails?.component?.componentName ||
                        assetDetails?.componentName ||
                        "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Asset No
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails.assetNo || "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Asset Main Description
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails.assetName || "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Purchase Date
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails.purchaseDate ? dayjs(assetDetails.purchaseDate).format("DD/MM/YYYY") : "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Description
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails.description || "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Project
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails.project || "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MDBox>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                      Asset By
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 1 }}>
                      {assetDetails.assetBy || "N/A"}
                    </MDTypography>
                  </MDBox>
                </Grid>

                <Grid item xs={12} >
                  <MDTypography
                    variant="h5"
                    sx={{ fontFamily: properties.fontFamily, fontWeight: "bold" }}
                  >
                    Assignment Information
                  </MDTypography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <MDBox sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <MDInput
                      label={<>Select Employee{" "}<span style={{ color: properties.colors.Red }}>*</span></>}
                      value={selectedEmployeeOption?.label || ""}
                      fullWidth
                      disabled
                      error={Boolean(errors.empId)}
                      helperText={errors.empId}
                    />
                    <MDButton
                      variant="contained"
                      color="info"
                      onClick={() => setEmployeeDialogOpen(true)}
                      sx={{ background: properties.colors.bgcolorbluethem, height: '38px', minHeight: '38px' }}
                    >
                      Find Employee
                    </MDButton>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        label={<>Assignment Date{" "}<span style={{ color: properties.colors.Red }}>*</span></>}
                        format="DD/MM/YYYY"
                        value={formData.assignedDate ? dayjs(formData.assignedDate) : null}
                        onChange={(date) => {
                          if (!date) return;

                          setFormData(prev => ({ ...prev, assignedDate: date.format("YYYY-MM-DD") }));
                          setErrors(prev => ({ ...prev, assignedDate: "" }));
                        }}
                        shouldDisableDate={(date) => {
                          const today = dayjs();
                          const purchaseDate = assetDetails.purchaseDate ? dayjs(assetDetails.purchaseDate) : null;
                          if (date.isAfter(today, "day")) return true;
                          if (purchaseDate && date.isBefore(purchaseDate, "day")) return true;
                          return false;
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.assignedDate,
                            helperText: errors.assignedDate,
                            sx: { fontFamily: properties.fontFamily },
                          }
                        }}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <MDInput
                    label="Comment/Reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ fontFamily: properties.fontFamily }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/assetList")}
                    sx={{ fontFamily: properties.fontFamily }}
                  >
                    Back to Asset List
                  </MDButton>
                  <MDButton
                    variant="contained"
                    color="info"
                    onClick={handleIssue}
                    disabled={loading || !selectedEmployeeOption}
                    sx={{
                      fontFamily: properties.fontFamily,
                      background: properties.colors.bgcolorbluethem,
                      "&:hover": {
                        background: properties.colors.bgcolorbluethem,
                        opacity: 0.9
                      }
                    }}
                  >
                    {loading ? "Issuing..." : "Issue Asset"}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </AppBar>

        <Snackbar
          open={open}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            autoHideDuration={1200}
            onClose={handleClose}
            severity={snakColor}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>

      <Dialog open={employeeDialogOpen} onClose={() => setEmployeeDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Select Employee</DialogTitle>
        <DialogContent>
          <MDBox sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Search by Name or ID"
              value={employeeQuery}
              onChange={(e) => setEmployeeQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { fetchEmployeePage(0, employeePageSize, e.currentTarget.value?.trim() || ""); } }}
              fullWidth
            />
            <MDButton
              variant="contained"
              color="info"
              onClick={() => fetchEmployeePage(0, employeePageSize, employeeQuery?.trim() || "")}
              sx={{ background: properties.colors.bgcolorbluethem }}
            >
              Search
            </MDButton>
          </MDBox>
          <div style={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={employeeRows}
              columns={[
                ...employeeColumns,
                {
                  field: "action",
                  headerName: "Action",
                  width: 120,
                  sortable: false,
                  renderCell: (params) => (
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        const emp = params.row;
                        const id = emp.empId || emp.id || emp.employeeId;
                        const name = emp.employeeName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
                        const label = `${name} [${id}]`;
                        setSelectedEmployeeOption({ id: String(id), label, raw: emp });
                        setFormData(prev => ({ ...prev, empId: String(id) }));
                        setErrors(prev => ({ ...prev, empId: "" }));
                        setEmployeeDialogOpen(false);
                      }}
                      size="small"
                    >
                      Select
                    </MDButton>
                  )
                }
              ]}
              getRowId={(row) => row._rid || row.empId || row.id || `${row.employeeId}-${row._rid}`}
              paginationMode="server"
              rowCount={employeeTotal}
              pageSizeOptions={[20, 50]}
              paginationModel={{ page: employeePage, pageSize: employeePageSize }}
              onPaginationModelChange={(model) => {
                const newPage = model.page;
                const newSize = model.pageSize;
                setEmployeePage(newPage);
                setEmployeePageSize(newSize);
                fetchEmployeePage(newPage, newSize, employeeQuery?.trim() || "");
              }}
              loading={empLoading}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setEmployeeDialogOpen(false)} color="secondary" variant="outlined">Close</MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default IssueAsset;
